import { useQueryClient, useInfiniteQuery, InfiniteData } from 'react-query'
import { v4 as uuidv4 } from 'uuid'
import { SelectedImage } from 'types'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'

export const RECOMMENDATION_CONFIG = {
  INITIAL_COUNT: 15,
  LOAD_MORE_COUNT: 10,
  MAX_PAGES: 20,
} as const

const CACHE_TIME_MS = 5 * 60 * 1000

interface UseBlazeRecommendationsProps {
  brandProfileId?: number
}

export interface BlazeRecommendation {
  id: string
  type: 'placeholder' | 'image'
  image?: SelectedImage
}

interface Page {
  pageIndex: number
  recommendations: BlazeRecommendation[]
}

const createPlaceholders = (count: number): BlazeRecommendation[] =>
  Array.from({ length: count }, () => ({
    id: `stock_image:${uuidv4()}`,
    type: 'placeholder' as const,
  }))

export const useBlazeRecommendations = ({ brandProfileId: propBrandProfileId }: UseBlazeRecommendationsProps = {}) => {
  const { data: brandProfile } = useBrandProfile({ cacheTime: CACHE_TIME_MS })
  const brandProfileId = propBrandProfileId ?? brandProfile?.id

  const queryClient = useQueryClient()
  const queryKey = ['blazeRecommendations', brandProfileId]

  const query = useInfiniteQuery<Page>(
    queryKey,
    ({ pageParam = 0 }) => {
      const isFirstPage = pageParam === 0
      const count = isFirstPage ? RECOMMENDATION_CONFIG.INITIAL_COUNT : RECOMMENDATION_CONFIG.LOAD_MORE_COUNT

      return Promise.resolve({
        pageIndex: pageParam,
        recommendations: createPlaceholders(count),
      })
    },
    {
      enabled: !!brandProfileId,
      cacheTime: CACHE_TIME_MS,
      staleTime: CACHE_TIME_MS,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.pageIndex + 1
        return nextPage < RECOMMENDATION_CONFIG.MAX_PAGES ? nextPage : undefined
      },
    },
  )

  const recommendations = query.data?.pages.flatMap((page) => page.recommendations) ?? []

  const updateRecommendation = (id: string, image: SelectedImage) => {
    queryClient.setQueryData<InfiniteData<Page> | undefined>(queryKey, (prev) => {
      if (!prev) return undefined
      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          recommendations: page.recommendations.map((rec) =>
            rec.id === id ? { id, type: 'image' as const, image } : rec,
          ),
        })),
      }
    })
  }

  const removeRecommendation = (id: string) => {
    queryClient.setQueryData<InfiniteData<Page> | undefined>(queryKey, (prev) => {
      if (!prev) return undefined
      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          recommendations: page.recommendations.filter((rec) => rec.id !== id),
        })),
      }
    })
  }

  const isGenerating = recommendations.some((rec) => rec.type === 'placeholder')

  return {
    recommendations,
    updateRecommendation,
    removeRecommendation,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage && !isGenerating,
    isGenerating,
    brandProfileId,
  }
}
